import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { submitLead, packFormFields } from '../lib/submitLead';
import { useLeadFormGuard } from '../hooks/useLeadFormGuard';
import { HoneypotField } from '../components/forms/HoneypotField';
import { usePageTitle } from '../hooks/usePageTitle';
import './ProvidersPage.css';

interface FormData {
  persona: string;
  needs: string[];
  location: string;
  timeframe: string;
  name: string;
  email: string;
  company: string;
  title: string;
  phone: string;
  notes: string;
  consent: boolean;
}

type FormStep = 1 | 2;
type SubmitState = 'idle' | 'loading' | 'success' | 'error';

const initialFormData: FormData = {
  persona: '',
  needs: [],
  location: '',
  timeframe: '',
  name: '',
  email: '',
  company: '',
  title: '',
  phone: '',
  notes: '',
  consent: false,
};

// Icons
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ArrowRight = () => (
  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const ArrowLeft = () => (
  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

// Custom Dropdown Component
interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  id?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({ options, value, onChange, placeholder, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
    } else if ((e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <div className={`custom-dropdown ${isOpen ? 'open' : ''}`} ref={dropdownRef} id={id}>
      <button
        type="button"
        className="dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={selectedOption ? 'dropdown-value' : 'dropdown-placeholder'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown />
      </button>
      {isOpen && (
        <div className="dropdown-menu" role="listbox">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`dropdown-option ${option.value === value ? 'selected' : ''}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
              {option.value === value && <CheckIcon />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProvidersPage: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle('Providers - Find a Certified Partner');
  const [step, setStep] = useState<FormStep>(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  // Mounted with the page, not with step 2 — the elapsed-time signal
  // should measure the whole journey, and a two-step form naturally
  // takes a human far longer than the threshold.
  const { honeypot, formProps, getGuardFields } = useLeadFormGuard();
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const personaOptions = [
    { value: 'law-firm', label: t('providers.form.personaOptions.lawFirm') },
    { value: 'trial-consultant', label: t('providers.form.personaOptions.trialConsultant') },
    { value: 'insurance-carrier', label: t('providers.form.personaOptions.insuranceCarrier') },
    { value: 'enterprise-risk', label: t('providers.form.personaOptions.enterpriseRisk') },
    { value: 'legal-tech', label: t('providers.form.personaOptions.legalTech') },
  ];

  const needsOptions = [
    { value: 'focus-groups', labelKey: 'providers.form.needsOptions.focusGroups' },
    { value: 'mock-trials', labelKey: 'providers.form.needsOptions.mockTrials' },
    { value: 'venue-modeling', labelKey: 'providers.form.needsOptions.venueModeling' },
    { value: 'expert-review', labelKey: 'providers.form.needsOptions.expertReview' },
    { value: 'fact-pattern', labelKey: 'providers.form.needsOptions.factPattern' },
    { value: 'integration', labelKey: 'providers.form.needsOptions.integration' },
    { value: 'other', labelKey: 'providers.form.needsOptions.other' },
  ];

  const timeframeOptions = [
    { value: 'immediate', label: t('providers.form.timeframeOptions.immediate') },
    { value: '30-days', label: t('providers.form.timeframeOptions.thirtyDays') },
    { value: '60-plus', label: t('providers.form.timeframeOptions.sixtyPlus') },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleNeedsToggle = (value: string) => {
    const currentNeeds = formData.needs;
    if (currentNeeds.includes(value)) {
      setFormData({
        ...formData,
        needs: currentNeeds.filter((n) => n !== value),
      });
    } else {
      setFormData({
        ...formData,
        needs: [...currentNeeds, value],
      });
    }
  };

  const handleStep1Continue = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitState('loading');
    setErrorMessage('');

    const packedMessage = packFormFields({
      persona: formData.persona,
      needs: formData.needs,
      location: formData.location,
      timeframe: formData.timeframe,
      company: formData.company,
      title: formData.title,
      phone: formData.phone,
      notes: formData.notes,
    });

    const result = await submitLead({
      name: formData.name,
      email: formData.email,
      message: packedMessage,
      ...getGuardFields(),
    });

    if (result.success) {
      setSubmitState('success');
    } else {
      setSubmitState('error');
      setErrorMessage(result.error || t('providers.form.errorGeneric'));
    }
  };

  const isStep1Valid =
    formData.persona && formData.needs.length > 0 && formData.location && formData.timeframe;

  const getPersonaLabel = (value: string) => {
    const option = personaOptions.find(o => o.value === value);
    return option ? option.label : value;
  };

  const getNeedLabel = (value: string) => {
    const option = needsOptions.find(o => o.value === value);
    return option ? t(option.labelKey) : value;
  };

  // Success State
  if (submitState === 'success') {
    return (
      <div className="providers-page">
        <section className="success-page">
          <div className="container">
            <div className="success-card">
              <div className="success-icon-wrapper">
                <CheckIcon />
              </div>
              <h1>{t('providers.confirmation.title')}</h1>
              <p className="success-message">{t('providers.confirmation.message1')}</p>
              <p className="success-detail">{t('providers.confirmation.message2')}</p>
              <Link to="/" className="btn btn-primary">
                {t('providers.confirmation.backToHome')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="providers-page">
      {/* Hero Section */}
      <section className="providers-hero">
        <div className="container">
          <span className="section-label">{t('providers.label')}</span>
          <h1>{t('providers.title')}</h1>
          <p className="providers-subtitle">{t('providers.subtitle')}</p>
        </div>
      </section>

      {/* Network Signals Section */}
      <section className="network-signals section">
        <div className="container">
          <div className="signals-grid">
            <div className="signal-card">
              <div className="signal-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3>{t('providers.signals.certified.title')}</h3>
                <p>{t('providers.signals.certified.description')}</p>
              </div>
            </div>
            <div className="signal-card">
              <div className="signal-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3>{t('providers.signals.implementation.title')}</h3>
                <p>{t('providers.signals.implementation.description')}</p>
              </div>
            </div>
            <div className="signal-card">
              <div className="signal-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              </div>
              <div>
                <h3>{t('providers.signals.whiteLabel.title')}</h3>
                <p>{t('providers.signals.whiteLabel.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="providers-form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-card">
              {/* Step Indicator */}
              <div className="form-steps">
                <div className={`form-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                  <span className="step-number">{step > 1 ? <CheckIcon /> : '1'}</span>
                  <span className="step-label">{t('providers.form.step1Label')}</span>
                </div>
                <div className={`form-step ${step >= 2 ? 'active' : ''}`}>
                  <span className="step-number">2</span>
                  <span className="step-label">{t('providers.form.step2Label')}</span>
                </div>
              </div>

              {/* Step 1: Search Selectors.
                  Step 1 doesn't submit, but it's the first thing a
                  visitor touches — spreading formProps warms reCAPTCHA
                  here so the script is long since ready by step 2. */}
              {step === 1 && (
                <form className="providers-form" onSubmit={handleStep1Continue} {...formProps}>
                  <div className="form-content">
                    <div className="form-group">
                      <label>{t('providers.form.persona')}</label>
                      <CustomDropdown
                        id="persona"
                        options={personaOptions}
                        value={formData.persona}
                        onChange={(value) => setFormData({ ...formData, persona: value })}
                        placeholder={t('providers.form.selectPlaceholder')}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('providers.form.needs')}</label>
                      <div className="needs-toggle-group">
                        {needsOptions.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            aria-pressed={formData.needs.includes(opt.value)}
                            className={`toggle-pill ${formData.needs.includes(opt.value) ? 'selected' : ''}`}
                            onClick={() => handleNeedsToggle(opt.value)}
                          >
                            {t(opt.labelKey)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="location">{t('providers.form.location')}</label>
                        <input
                          type="text"
                          id="location"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          placeholder={t('providers.form.locationPlaceholder')}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>{t('providers.form.timeframe')}</label>
                        <CustomDropdown
                          options={timeframeOptions}
                          value={formData.timeframe}
                          onChange={(value) => setFormData({ ...formData, timeframe: value })}
                          placeholder={t('providers.form.selectPlaceholder')}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-footer">
                    <div className="form-nav">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!isStep1Valid}
                      >
                        {t('providers.form.findProvider')}
                        <ArrowRight />
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Step 2: Contact Capture */}
              {step === 2 && (
                <form className="providers-form" onSubmit={handleSubmit} {...formProps}>
                  <HoneypotField {...honeypot} />
                  <div className="form-content">
                    {/* Search Summary */}
                    <div className="search-summary">
                      <div className="search-summary-title">{t('providers.form.searchCriteria')}</div>
                      <div className="search-summary-items">
                        <span className="summary-tag primary">{getPersonaLabel(formData.persona)}</span>
                        {formData.needs.map(need => (
                          <span key={need} className="summary-tag">{getNeedLabel(need)}</span>
                        ))}
                        <span className="summary-tag">{formData.location}</span>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">{t('providers.form.name')}</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder={t('providers.form.namePlaceholder')}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">{t('providers.form.email')}</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder={t('providers.form.emailPlaceholder')}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="company">{t('providers.form.company')}</label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder={t('providers.form.companyPlaceholder')}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="title">{t('providers.form.title')}</label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder={t('providers.form.titlePlaceholder')}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">{t('providers.form.phone')}</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder={t('providers.form.phonePlaceholder')}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="notes">{t('providers.form.notes')}</label>
                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder={t('providers.form.notesPlaceholder')}
                      />
                    </div>

                    <div className="consent-group">
                      <label className={`consent-checkbox ${formData.consent ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          className="consent-input-sr"
                          checked={formData.consent}
                          onChange={(e) =>
                            setFormData({ ...formData, consent: e.target.checked })
                          }
                          required
                        />
                        <span className="checkbox-box" aria-hidden="true">
                          {formData.consent && <CheckIcon />}
                        </span>
                        <span className="checkbox-text">{t('providers.form.consent')}</span>
                      </label>
                    </div>

                    {submitState === 'error' && (
                      <div className="form-error">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {errorMessage}
                      </div>
                    )}
                  </div>

                  <div className="form-footer">
                    <div className="form-nav">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleBack}
                      >
                        <ArrowLeft />
                        {t('providers.form.back')}
                      </button>
                      <button
                        type="submit"
                        className={`btn btn-primary ${submitState === 'loading' ? 'btn-loading' : ''}`}
                        disabled={submitState === 'loading' || !formData.consent}
                      >
                        {submitState === 'loading' ? (
                          <>
                            <span className="btn-spinner"></span>
                            {t('providers.form.submitting')}
                          </>
                        ) : (
                          <>
                            {t('providers.form.requestMatch')}
                            <ArrowRight />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProvidersPage;
